/**
 * The infuser: a machine that lays blocks onto the ground around it. A slate plinth, three legs
 * holding up an open basket of bars where the blocks it was given pile up (the pile rises as it is
 * loaded), a chute down the middle, and a spreader arm at its foot that turns while it lays, its
 * nozzle glowing green. Offsets are in machine space, y up from the tile's top, a tile about 1.2
 * wide.
 */
import * as THREE from 'three';

import { around, merged, part, type MachinePart } from './part';

const SLATE = 0x3b4150;
const BRASS = 0xc9b27a;
const DARK_BRASS = 0x8f7d52;
/** The blocks in its basket: mixed, earthy. */
const BLOCKS = 0x9a8f80;
/** Its nozzle: the green of the infuser (core/palette.ts). */
const GREEN = 0xa6dd7a;
const GREEN_GLOW = 0x6fae3a;

/** The whole machine's size against the measures below. */
const SIZE = 1.3;
const PLINTH = 0.05;
/** The basket: where its floor is, how tall, how wide. */
const BASKET_Y = 0.3;
const BASKET_TALL = 0.2;
const BASKET_R = 0.15;
/** The block pile in the basket, empty to full: its height at full, at the machine's size. */
export const PILE_FULL = 0.18 * SIZE;

const THIRDS = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3] as const;
const SIXTHS = [0, 1, 2, 3, 4, 5].map((k) => (k * Math.PI) / 3);

/** A part at the machine's size; the block pile keeps its unit height (the props scale it to how full). */
function sized(p: MachinePart, unitTall = false): MachinePart {
  p.geometry.scale(SIZE, unitTall ? 1 : SIZE, SIZE);
  return { ...p, offset: [p.offset[0] * SIZE, p.offset[1] * SIZE, p.offset[2] * SIZE] };
}

/** Three legs from the plinth to the basket's floor, leaning in. */
function legs(): THREE.BufferGeometry {
  const tall = BASKET_Y - PLINTH;
  const lean = Math.atan2(0.06, tall);
  return around(
    () => new THREE.BoxGeometry(0.03, tall + 0.02, 0.03).translate(0, tall / 2, 0).rotateZ(lean),
    THIRDS,
    0.18,
  ).translate(0, PLINTH, 0);
}

/** The basket: a floor, a ring at its top and bars between them, open so the pile shows. */
function basket(): THREE.BufferGeometry {
  return merged([
    new THREE.CylinderGeometry(BASKET_R * 0.8, BASKET_R * 0.6, 0.03, 12).translate(0, 0.015, 0),
    new THREE.TorusGeometry(BASKET_R, 0.014, 6, 18)
      .rotateX(Math.PI / 2)
      .translate(0, BASKET_TALL, 0),
    around(
      () =>
        new THREE.BoxGeometry(0.018, BASKET_TALL, 0.018)
          .translate(0, BASKET_TALL / 2, 0)
          .rotateZ(-0.18),
      SIXTHS,
      BASKET_R * 0.8,
    ),
  ]);
}

/** Blocks piled in the basket, a unit tall from their foot: scaled to how full it is. */
function pile(): THREE.BufferGeometry {
  return merged([
    new THREE.BoxGeometry(0.16, 0.5, 0.16).translate(0, 0.25, 0),
    new THREE.BoxGeometry(0.11, 0.5, 0.11).rotateY(0.5).translate(0.02, 0.75, -0.01),
  ]);
}

/** The spreader: a hub on the chute's foot, an arm out to one side and a nozzle pointing down. */
function spreader(): THREE.BufferGeometry {
  return merged([
    new THREE.CylinderGeometry(0.045, 0.045, 0.04, 10),
    new THREE.BoxGeometry(0.24, 0.025, 0.035).translate(0.12, 0, 0),
    new THREE.CylinderGeometry(0.025, 0.012, 0.06, 8).translate(0.24, -0.03, 0),
  ]);
}

/** The blocks in its basket, rising as it is loaded. */
export const INFUSER_PILE: MachinePart = sized(
  part('inf.pile', pile(), BLOCKS, [0, BASKET_Y + 0.03, 0], { roughness: 0.85, metalness: 0 }),
  true,
);

/** The spreader arm, turning while it lays. */
export const INFUSER_ARM: MachinePart = sized(
  part('inf.arm', spreader(), DARK_BRASS, [0, PLINTH + 0.1, 0], {
    metalness: 0.6,
    roughness: 0.4,
    spin: 'y',
    speed: 1.6,
  }),
);

/** The parts that stand still. */
export const INFUSER_FRAME: readonly MachinePart[] = [
  part('inf.base', new THREE.CylinderGeometry(0.2, 0.24, PLINTH, 6), SLATE, [0, PLINTH / 2, 0], {
    metalness: 0.35,
  }),
  part('inf.legs', legs(), BRASS, [0, 0, 0], { metalness: 0.5, roughness: 0.4 }),
  part('inf.basket', basket(), BRASS, [0, BASKET_Y, 0], { metalness: 0.5, roughness: 0.4 }),
  part(
    'inf.chute',
    new THREE.CylinderGeometry(0.03, 0.04, BASKET_Y - PLINTH - 0.1, 8),
    DARK_BRASS,
    [0, PLINTH + 0.12 + (BASKET_Y - PLINTH - 0.1) / 2, 0],
    { metalness: 0.6, roughness: 0.4 },
  ),
  part(
    'inf.lamp',
    new THREE.OctahedronGeometry(0.035),
    GREEN,
    [0, BASKET_Y + BASKET_TALL + 0.09, 0],
    {
      roughness: 0.3,
      glow: GREEN_GLOW,
      pulse: true,
    },
  ),
].map((p) => sized(p));
